import { useEffect, useMemo, useState } from 'react';
import SiteHeader from '../components/SiteHeader';
import FileBrowseField from '../components/FileBrowseField';
import demoCatalog from '../data/demoTools.json';
import demoRecipes from '../data/demoRecipes.json';

function toolHaystack(tool, provider) {
  return [
    tool.name,
    tool.group,
    tool.description,
    tool.id,
    provider,
    tool.kind,
  ].filter(Boolean).join(' ').toLowerCase();
}

function ParameterPreview({
  tool,
  paramValues,
  setParamValues,
  onApplyRecipe,
}) {
  const [runMessage, setRunMessage] = useState('');
  const relatedRecipes = useMemo(
    () => (tool ? demoRecipes.filter((r) => r.modelId === tool.id) : []),
    [tool],
  );

  if (!tool) {
    return (
      <div className="toolbox-detail-empty panel-card">
        <h2>Select a tool</h2>
        <p>
          Choose a <strong>custom model</strong> or QGIS algorithm from the catalog.
          Browse files to attach filenames only — nothing is uploaded on this demo site.
        </p>
      </div>
    );
  }

  function updateParam(name, value) {
    setParamValues((prev) => ({ ...prev, [name]: value }));
  }

  function handleRun() {
    setRunMessage(
      'Demo only — in a real install this job would run through QGIS Processing. No data was processed here.',
    );
  }

  return (
    <div className="toolbox-detail panel-card">
      <div className="toolbox-detail-head">
        <div>
          <p className="toolbox-detail-provider">{tool.provider}</p>
          <h2>{tool.name}</h2>
          <p className="toolbox-detail-group">
            {tool.group}
            {tool.kind === 'model' ? ' · Custom model' : ''}
          </p>
        </div>
        <span className="demo-badge">Demo preview</span>
      </div>
      <p className="toolbox-detail-desc">{tool.description}</p>
      <p className="qgis-powered-inline">
        <img src="/qgis-logo.svg" alt="" className="qgis-powered-logo" width="28" height="28" />
        Executed by <strong>QGIS Processing</strong>.
      </p>

      {relatedRecipes.length > 0 ? (
        <div className="recipe-strip">
          <h3>Recipes for this tool</h3>
          <div className="recipe-strip-list">
            {relatedRecipes.map((recipe) => (
              <button
                key={recipe.id}
                type="button"
                className="recipe-chip"
                onClick={() => onApplyRecipe(recipe)}
              >
                <strong>{recipe.name}</strong>
                <span>Load recipe</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <form className="toolbox-params" onSubmit={(e) => e.preventDefault()}>
        <h3>Parameters</h3>
        {tool.parameters.map((param) => {
          const value = paramValues[param.name] ?? (param.type === 'boolean' ? String(!!param.value) : (param.value ?? ''));
          if (param.type === 'file') {
            return (
              <FileBrowseField
                key={param.name}
                label={param.label}
                value={value}
                onChange={(name) => updateParam(param.name, name)}
              />
            );
          }
          if (param.type === 'boolean') {
            return (
              <label key={param.name} className="form-field">
                <span>{param.label}</span>
                <select
                  value={value === true || value === 'true' ? 'true' : 'false'}
                  onChange={(e) => updateParam(param.name, e.target.value)}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </label>
            );
          }
          if (param.type === 'output') {
            return (
              <label key={param.name} className="form-field">
                <span>{param.label}</span>
                <input type="text" value={String(value)} readOnly />
              </label>
            );
          }
          return (
            <label key={param.name} className="form-field">
              <span>{param.label}</span>
              <input
                type="text"
                value={String(value)}
                onChange={(e) => updateParam(param.name, e.target.value)}
              />
            </label>
          );
        })}
        <div className="toolbox-actions">
          <button type="button" className="btn btn-primary" onClick={handleRun}>
            Run
          </button>
          <button type="button" className="btn btn-outline" disabled title="Demo preview">
            Save recipe
          </button>
        </div>
        {runMessage ? <p className="demo-run-msg">{runMessage}</p> : null}
        <p className="field-hint">
          Browse selects a filename only. GIS Liberty never uploads or reads your files on this website.
        </p>
      </form>
    </div>
  );
}

export default function ToolboxPage() {
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState('model:watershed');
  const [paramValues, setParamValues] = useState({});
  const [recipeNotice, setRecipeNotice] = useState('');
  const [pendingRecipe, setPendingRecipe] = useState(null);

  const flatTools = useMemo(
    () => demoCatalog.flatMap((section) => section.tools.map((tool) => ({
      ...tool,
      provider: section.provider,
    }))),
    [],
  );

  const filteredSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return demoCatalog;
    return demoCatalog
      .map((section) => ({
        ...section,
        tools: section.tools.filter((tool) => toolHaystack(tool, section.provider).includes(q)),
      }))
      .filter((section) => section.tools.length > 0);
  }, [query]);

  const selectedTool = flatTools.find((tool) => tool.id === selectedId) || null;

  useEffect(() => {
    if (!selectedTool) {
      setParamValues({});
      return;
    }
    const next = {};
    selectedTool.parameters.forEach((param) => {
      if (param.type === 'boolean') next[param.name] = param.value ? 'true' : 'false';
      else if (param.type === 'file') next[param.name] = '';
      else next[param.name] = param.value ?? '';
    });
    if (pendingRecipe && pendingRecipe.modelId === selectedTool.id) {
      Object.assign(next, pendingRecipe.values);
      setRecipeNotice(`Loaded recipe “${pendingRecipe.name}” — filenames only, no data loaded.`);
      setPendingRecipe(null);
    } else {
      setRecipeNotice('');
    }
    setParamValues(next);
  }, [selectedTool?.id]);

  function applyRecipe(recipe) {
    if (selectedId === recipe.modelId) {
      setParamValues((prev) => ({ ...prev, ...recipe.values }));
      setRecipeNotice(`Loaded recipe “${recipe.name}” — filenames only, no data loaded.`);
      return;
    }
    setPendingRecipe(recipe);
    setSelectedId(recipe.modelId);
  }

  return (
    <div className="site-page site-page-app">
      <SiteHeader variant="app" />

      <div className="demo-banner">
        Interactive UI preview — powered by QGIS Processing in a real install. No data is uploaded here.
      </div>

      <main className="toolbox-layout toolbox-layout-3">
        <aside className="toolbox-sidebar panel-card">
          <div className="toolbox-sidebar-head">
            <h1>Toolbox</h1>
            <p className="field-hint">{flatTools.length} tools · custom models first</p>
          </div>
          <label className="form-field toolbox-search">
            <span className="sr-only">Search tools</span>
            <input
              type="search"
              placeholder="Search algorithms and models…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>

          <div className="toolbox-tree">
            {filteredSections.map((section) => (
              <section key={section.provider} className="toolbox-provider">
                <h2>{section.provider}</h2>
                <ul>
                  {section.tools.map((tool) => (
                    <li key={tool.id}>
                      <button
                        type="button"
                        className={`toolbox-tool${selectedId === tool.id ? ' active' : ''}`}
                        onClick={() => setSelectedId(tool.id)}
                      >
                        <span className="toolbox-tool-name">
                          {tool.name}
                          {tool.kind === 'model' ? <span className="model-pill">Model</span> : null}
                        </span>
                        <span className="toolbox-tool-group">{tool.group}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
            {filteredSections.length === 0 ? (
              <p className="field-hint">No tools match your search.</p>
            ) : null}
          </div>
        </aside>

        <section className="toolbox-main">
          {recipeNotice ? <p className="recipe-notice panel-card">{recipeNotice}</p> : null}
          <ParameterPreview
            tool={selectedTool}
            paramValues={paramValues}
            setParamValues={setParamValues}
            onApplyRecipe={applyRecipe}
          />
        </section>

        <aside className="recipes-panel panel-card">
          <h2>Saved recipes</h2>
          <p className="field-hint">
            Recipes store parameter presets for a model or algorithm — including filenames only.
          </p>
          <ul className="recipe-list">
            {demoRecipes.map((recipe) => (
              <li key={recipe.id}>
                <button type="button" className="recipe-card" onClick={() => applyRecipe(recipe)}>
                  <strong>{recipe.name}</strong>
                  <span className="recipe-card-model">{recipe.modelName}</span>
                  <span className="recipe-card-desc">{recipe.description}</span>
                </button>
              </li>
            ))}
          </ul>
        </aside>
      </main>
    </div>
  );
}
